import { collection, deleteDoc, doc, onSnapshot, orderBy, query, serverTimestamp, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { experimentStamp } from './experiments';
import { db, functions } from '../lib/firebase';

/**
 * Individual readings, kept as they were measured.
 *
 * A formulation holds one value per quantity, which suits an instrument that
 * reports one. A plate reader reports six wells for one sample and no average
 * anywhere, and collapsing those at import would throw away the only thing that
 * makes a spread meaningful. So each reading is its own record, tagged with the
 * sample it belongs to and which replicate it was.
 *
 * They live beside formulations rather than inside them because they are not
 * the same shape: a reading belongs to a reader's field set, which is whatever
 * the person who defined that reader decided, and no fixed document could hold
 * every version of that.
 */

export interface Reading {
  id: string;
  readerId: string;
  readerName: string;
  sampleLabel: string;
  /** Header levels that made up the name, outermost first. */
  labelParts: string[];
  replicate: number;
  /** The measurement — or, for a summary, the average. */
  values: Record<string, number>;
  /** Summary only: SD per field, and how many measurements the average is from. */
  sd?: Record<string, number>;
  n?: number | null;
  summary?: boolean;
  captureId: string | null;
  capturedByUid: string | null;
  /** The experiment that was open when it was captured, if any. */
  experimentId?: string;
  createdAt: unknown;
}

export const readingsCol = (projectId: string) => collection(db, 'projects', projectId, 'readings');

export function watchReadings(projectId: string, cb: (r: Reading[]) => void) {
  const q = query(readingsCol(projectId), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snap) =>
    cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Reading)),
  );
}

export interface ReadingDraft {
  sampleLabel: string;
  labelParts: string[];
  replicate: number;
  values: Record<string, number>;
  sd?: Record<string, number>;
  n?: number | null;
  summary?: boolean;
}

/**
 * Write a confirmed set of readings.
 *
 * Batched because a plate is a hundred documents and a partial write would
 * leave a sample half-recorded — worse than not recorded, because nothing looks
 * wrong about it. Firestore caps a batch at 500, which is comfortably more than
 * a 96-well plate.
 */
export async function saveReadings(
  projectId: string,
  captureId: string | null,
  uid: string,
  reader: { id: string; name: string },
  drafts: ReadingDraft[],
): Promise<number> {
  const chunks: ReadingDraft[][] = [];
  for (let i = 0; i < drafts.length; i += 400) chunks.push(drafts.slice(i, i + 400));

  for (const chunk of chunks) {
    const batch = writeBatch(db);
    for (const d of chunk) {
      batch.set(doc(readingsCol(projectId)), {
        readerId: reader.id,
        readerName: reader.name,
        sampleLabel: d.sampleLabel,
        labelParts: d.labelParts,
        replicate: d.replicate,
        values: d.values,
        ...(d.summary ? { summary: true, sd: d.sd ?? {}, n: d.n ?? null } : {}),
        captureId,
        capturedByUid: uid,
        ...experimentStamp(projectId),
        createdAt: serverTimestamp(),
      });
    }
    await batch.commit();
  }
  return drafts.length;
}

export interface ReaderExtraction {
  readerId: string;
  reader: { id: string; name: string; fields: { key: string; label: string; unit?: string }[] };
  orientation: string;
  confidence: 'high' | 'medium' | 'low';
  /** Absent on reads made before summaries were recognised: treat as raw. */
  dataShape?: 'raw' | 'summary';
  columnsSeen: { heading: string; mappedTo: string | null }[];
  readings: {
    sampleLabel: string;
    labelParts: string[];
    replicate: number;
    values: Record<string, number>;
    sd?: Record<string, number>;
    n?: number | null;
  }[];
  unreadable: string[];
}

type ReaderExtractResponse =
  | { ok: true; data: ReaderExtraction }
  | { ok: false; error: string; rejected?: boolean };

const callReaderExtract = httpsCallable<Record<string, unknown>, ReaderExtractResponse>(
  functions,
  'extractWithReader',
);

export class ReaderExtractionError extends Error {
  constructor(
    message: string,
    /** The model refused the image rather than failing — a different remedy. */
    readonly rejected: boolean,
  ) {
    super(message);
    this.name = 'ReaderExtractionError';
  }
}

export async function runReaderExtraction(req: {
  projectId: string;
  captureId: string;
  readerId: string;
  storagePath?: string;
  spokenText?: string;
  transcript?: string;
}): Promise<ReaderExtraction> {
  let res;
  try {
    res = await callReaderExtract(req);
  } catch (e) {
    throw new ReaderExtractionError((e as Error)?.message ?? 'Extraction failed.', false);
  }
  if (!res.data?.ok) {
    const failed = res.data as { error?: string; rejected?: boolean };
    throw new ReaderExtractionError(failed?.error ?? 'Nothing could be read.', !!failed?.rejected);
  }
  return res.data.data;
}

/**
 * Tell the learning store what a human did to a read.
 *
 * Fire and forget. It improves later reads and must never be able to interfere
 * with this one, so the caller does not wait for it and failures are swallowed.
 */
const callRecordConfirmation = httpsCallable(functions, 'recordConfirmation');

export function reportConfirmation(
  readerId: string,
  extracted: ReaderExtraction['readings'],
  confirmed: ReadingDraft[],
): void {
  void callRecordConfirmation({
    readerId,
    extracted: { readings: extracted },
    confirmed: { readings: confirmed },
  }).catch(() => {});
}

/** Remove one reading. The rules allow only the project owner. */
export async function deleteReading(projectId: string, readingId: string) {
  await deleteDoc(doc(readingsCol(projectId), readingId));
}

export interface LearnedReader {
  reader: {
    id: string;
    name: string;
    description: string;
    fields: import('../lib/types').ReaderField[];
  };
  extraction: ReaderExtraction;
  /** Summary sheets only: whether a count was printed. */
  nDetected: boolean;
}

const callLearnReader = httpsCallable<
  Record<string, unknown>,
  { ok: true; data: LearnedReader } | { ok: false; error: string; rejected?: boolean }
>(functions, 'learnReader');

/** Learn a reader from one trial photo, which must already be uploaded. */
export async function learnReaderFromPhoto(req: {
  projectId: string;
  captureId: string;
  readerId: string;
  name: string;
  storagePath: string;
}): Promise<LearnedReader> {
  let res;
  try {
    res = await callLearnReader(req);
  } catch (e) {
    throw new ReaderExtractionError((e as Error)?.message ?? 'Could not learn from that photo.', false);
  }
  if (!res.data?.ok) {
    const failed = res.data as { error?: string; rejected?: boolean };
    throw new ReaderExtractionError(failed?.error ?? 'Nothing could be learned.', !!failed?.rejected);
  }
  return res.data.data;
}

